import { FC } from 'react';
import classNames from 'classnames';

import styles from '../../opm-summary-waterfall-erf.module.scss';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
  numberCalculatedViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import {
  FirstLastSummaryCalculations,
  FirstLastSummaryIndexDictionaryDto,
  OpmFirstLastWaterfallDictionaryDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { useLocale } from '@app/core/hooks/useLocale';
import { translationKeys } from '@locale/setupI18n';
import { simulationSectionsDefinition } from '../helper/FirstLastHelpers';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

type ValueKeys = Omit<FirstLastSummaryCalculations, 'instruments'>;

interface SummaryCalculationsProps {
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  isEquitySimulation?: boolean;
}

const SummaryCalculationsErf: FC<SummaryCalculationsProps> = ({
  opmWaterfall,
  selectedYear,
  isEquitySimulation = false,
}): JSX.Element => {
  const { l } = useLocale();
  const exitSimulationTitleKey = isEquitySimulation ? '_ExitEquity' : '_ExitEV';
  const exitSimulationTitle: translationKeys = exitSimulationTitleKey;

  const createRow = (
    label: translationKeys,
    valueKey: keyof ValueKeys,
    isPercentage = false,
    className = '',
    decimalPoint: number | null = null
  ) => {
    return (
      <tr>
        <td
          className={classNames(className, styles['border-right'])}
          data-testid={`${valueKey}-title`}>
          {l(label)}
        </td>
        {simulationSectionsDefinition.map((section) =>
          Object.entries(opmWaterfall[selectedYear][section]).map(([key, _]) => {
            const value = (
              opmWaterfall[selectedYear][section] as FirstLastSummaryIndexDictionaryDto
            )[key][valueKey];

            const cellClass = classNames(className, {
              [styles['border-right']]: key === '5',
            });

            return (
              <td
                key={`${section}-${key}`}
                className={cellClass}
                data-testid={`${valueKey}-simulation-${key}`}>
                <CellValue
                  value={
                    isPercentage && typeof value === 'number'
                      ? getCalculatedProjectValueInPercent(value)
                      : value
                  }
                  {...(isPercentage ? enteredPercentageViewRules : amountRules)}
                  {...(decimalPoint !== null && numberCalculatedViewRules)}
                  normalize
                />
              </td>
            );
          })
        )}
      </tr>
    );
  };

  return (
    <>
      {createRow(exitSimulationTitle, 'simulatedEv')}
      {createRow('_TransactionCost', 'transactionCostsPc', true)}
      {createRow('_IpoDiscount', 'ipoDiscountPc', true)}
      {createRow('_NetExitProceeds', 'netExitProceeds')}
      {!isEquitySimulation && createRow('_NetDebtNegativeOrNetCash', 'totalNetCash')}
      {createRow('_TotalEquityTitle', 'totalEquityProceeds')}
      {createRow('_TotalContractualShareholderDebt', 'totalShareholderDebt')}
      {createRow('_OrdinaryEquity', 'totalOrdinaryEquityProceeds')}
    </>
  );
};

export default SummaryCalculationsErf;
